import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// PACKAGES
import { useFormik } from 'formik';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import FileUpload from '../../../components/FileUpload';
import FilesList from '../../../components/FilesList';
import Button from '../../../components/Button';
import DateTimeInput from '../../../components/DateTimeInput';
import LoadingSpinner from '../../../components/LoadingSpinner';

// TYPES
import { ComplaintsProgressViewTypes, ViewTypes } from './ComplaintsProgressModal';
import { ComplaintEditActionCommand } from '../../../api/models';

const UpdateActivity: React.FC<ComplaintsProgressViewTypes> = ({
	complaintId,
	activity = {},
	showView,
	closeModal,
	refreshComplaintsProgress,
	getComplaintsDetailsData
}) => {
	const { id = '', title = '', occurrence = '', description = '', attachments = [] } = activity;
	const createAttachments: ComplaintEditActionCommand['createAttachments'] = [];
	const deleteAttachmentIds: ComplaintEditActionCommand['deleteAttachmentIds'] = [];

	const mode = !id ? 'create' : 'update';

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const logComplaintsProgress = async (values: ComplaintEditActionCommand) => {
		if (values !== undefined) {
			setIsLoading(true);

			const response = await apibridge.logComplaintsProgress(values);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);
			}
		}
	};

	const logProgress = async (values: ComplaintEditActionCommand) => {
		await logComplaintsProgress(values);

		refreshComplaintsProgress();

		getComplaintsDetailsData();

		if (closeModal) {
			closeModal();
		}
	};

	const initialValues: ComplaintEditActionCommand = {
		id,
		complaintId,
		title,
		occurrence,
		description,
		createAttachments,
		deleteAttachmentIds
	};

	const formik = useFormik({
		initialValues,
		validate: (values) => {
			const errors: any = {};

			if (!values.title) {
				errors.title = 'Required';
			}

			if (!values.description) {
				errors.description = 'Required';
			}

			return errors;
		},
		onSubmit: (values) => {
			logProgress(values);
		}
	});

	return (
		<form onSubmit={formik.handleSubmit}>
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="form-group">
									<label htmlFor="title">Title of Activity</label>

									<input
										className="form-control"
										name="title"
										id="title"
										type="text"
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.title || ''}
										placeholder="E.g. Phone call with parent"
									/>

									{formik.touched.title && formik.errors.title ? (
										<div className="form-validation-error">{formik.errors.title}</div>
									) : null}
								</div>

								<div className="form-group">
									<label htmlFor="occurrence">Date and time of the activity</label>

									<DateTimeInput
										dateTimeString={formik.values.occurrence || ''}
										onChange={(val: string) => formik.setFieldValue('occurrence', val)}
									/>
								</div>

								<div className="form-group">
									<label htmlFor="description">Description of activity</label>

									<textarea
										className="form-control"
										name="description"
										id="description"
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.description}
									></textarea>

									{formik.touched.description && formik.errors.description ? (
										<div className="form-validation-error">{formik.errors.description}</div>
									) : null}
								</div>

								<div className="form-group">
									<label htmlFor="createAttachments">
										Attachments <span className="note">(optional)</span>
									</label>

									<FilesList
										files={attachments}
										onChange={(vals: string[]) => formik.setFieldValue('deleteAttachmentIds', vals)}
										multiple={true}
									/>

									<FileUpload
										name="createAttachments"
										onChange={(vals: string[]) => formik.setFieldValue('createAttachments', vals)}
										multiple={true}
										maxSizeMb={5}
									></FileUpload>
								</div>

								<section className="actions">
									<Button className="btn-primary full-width" type="submit">
										Log action
									</Button>

									{mode === 'update' && (
										<Button className="btn-secondary full-width" onClick={() => showView(ViewTypes.Read)}>
											Cancel
										</Button>
									)}
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</form>
	);
};

export default UpdateActivity;
